import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/useDebounce";

interface LivePdfPreviewProps {
  formData: Record<string, unknown>;
  event?: {
    name: string;
    event_date: string;
    event_time?: string | null;
    location: string;
    event_type: string[];
    expected_attendees?: number | null;
  };
  organization?: {
    name: string;
  };
}

export function LivePdfPreview({
  formData,
  event,
  organization,
}: LivePdfPreviewProps) {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const debouncedFormData = useDebounce(formData, 1000);

  const generatePreview = useCallback(async () => {
    if (!event || !organization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "/.netlify/functions/generateTechnicalSheetPdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event,
            organization,
            technicalSheet: formData,
          }),
        },
      );

      if (response.ok) {
        const html = await response.text();
        setHtmlContent(html);
      } else {
        setError("Erreur lors de la génération de l'aperçu");
      }
    } catch (err) {
      console.error("Preview error:", err);
      setError("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  }, [formData, event, organization]);

  useEffect(() => {
    if (event && organization) {
      generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData, event, organization]);

  return (
    <div className="h-full flex flex-col bg-slate-50 border-l">
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div>
          <h3 className="font-semibold text-sm">Aperçu PDF en direct</h3>
          <p className="text-xs text-muted-foreground">
            Mise à jour automatique
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={generatePreview}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {loading && !htmlContent && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Génération de l'aperçu...
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="text-center">
              <p className="text-sm text-destructive mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={generatePreview}>
                Réessayer
              </Button>
            </div>
          </div>
        )}

        {htmlContent && (
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            className="w-full h-full border-0"
            title="Aperçu PDF de la fiche technique"
          />
        )}
      </div>

      {loading && htmlContent && (
        <div className="absolute top-20 right-8 bg-white shadow-lg rounded-full px-3 py-1 flex items-center gap-2 text-xs">
          <Loader2 className="h-3 w-3 animate-spin text-primary" />
          <span className="text-muted-foreground">Actualisation...</span>
        </div>
      )}
    </div>
  );
}
